/**
 * Kanban-доска бэклога: столбцы по статусам модели и перетаскивание карточек.
 *
 * Flow: App загружает карточки → доска раскладывает их по столбцам →
 *       перетаскивание вызывает смену статуса на сервере → App перезагружает
 *       список.
 *
 * Перетаскивание — на нативном HTML5 drag&drop, без библиотеки: доска
 * перекладывает карточки между девятью списками, и ради этого тянуть
 * dnd-kit (сотни килобайт) незачем.
 *
 * Зависимости:
 *   - react: состояние подсветки столбца при переносе;
 *   - ./model: столбцы, цвета приоритетов, локальная проверка перехода.
 */
import { useState } from 'react'
import { COLUMNS, priorityColor, transitionBlocker } from './model.js'

function Card({ item, onOpen, onDragStart, readOnly }) {
  const needsContext = !item.has_session_context && item.status !== 'backlog'
  return (
    <article
      className="item"
      style={{ borderLeftColor: priorityColor(item.priority) }}
      draggable={!readOnly}
      onDragStart={(e) => onDragStart(e, item)}
      onClick={() => onOpen(item.id)}
    >
      <div className="title">{item.title}</div>
      <div className="meta">
        <span className="badge">{item.priority}</span>
        <span className="badge">{item.type}</span>
        {item.assignee && <span>{item.assignee.replace('developer:', '')}</span>}
        {needsContext && <span className="badge warn" title="Нет session-context">без контекста</span>}
        {item.blocked_by && <span className="badge warn">{item.blocked_by}</span>}
        {/* Прикреплённая мелочь не имеет места в очереди и едет вместе
            с базовой карточкой — на доске это должно быть видно сразу,
            иначе она выглядит забытой (навык /backlog §4.7). */}
        {item.parent && (
          <span className="badge" title={`Часть работы над ${item.parent}`}>
            ⤷ {item.parent.replace('BL-', '')}
          </span>
        )}
      </div>
    </article>
  )
}

export default function Board({ items, onOpen, onMove, readOnly }) {
  const [dragOver, setDragOver] = useState(null)
  const [dragged, setDragged] = useState(null)

  function handleDragStart(event, item) {
    setDragged(item)
    event.dataTransfer.effectAllowed = 'move'
    // Данные в dataTransfer нужны, чтобы браузер считал перенос состоявшимся;
    // саму карточку держим в состоянии — так доступен весь объект, а не только id.
    event.dataTransfer.setData('text/plain', item.id)
  }

  function handleDrop(event, columnKey) {
    event.preventDefault()
    setDragOver(null)
    if (!dragged || dragged.status === columnKey) return
    onMove(dragged, columnKey)
    setDragged(null)
  }

  return (
    <div className="board">
      {COLUMNS.map((column) => {
        const columnItems = items.filter((i) => i.status === column.key)
        // Подсказка о запрете показывается ещё до броска — пользователь видит,
        // что перенос не пройдёт, а не узнаёт об этом из отката.
        const blocked = dragged ? transitionBlocker(dragged, column.key) : null
        return (
          <section
            key={column.key}
            className={`column${dragOver === column.key ? ' drop' : ''}`}
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(column.key)
            }}
            onDragLeave={() => setDragOver((c) => (c === column.key ? null : c))}
            onDrop={(e) => handleDrop(e, column.key)}
            title={dragOver === column.key && blocked ? blocked : column.hint}
          >
            <h2>
              {column.title}
              <span className="count">{columnItems.length}</span>
            </h2>
            <div className="list">
              {columnItems.map((item) => (
                <Card
                  key={item.id}
                  item={item}
                  onOpen={onOpen}
                  onDragStart={handleDragStart}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}
